import { useState, useMemo } from 'react'
import { Head, router } from '@inertiajs/react'
import AppShell, { type AppShellProps } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Search, Plus, Clock, Users, ArrowLeft, Download } from 'lucide-react'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface CheckInRecord {
  id: number
  firstName: string | null
  lastName: string | null
  email: string | null
  organization: string | null
  position: string | null
  token: string | null
  zoneName: string | null
  checkInTime: string | null
}

interface Zone {
  id: number
  name: string | null
}

interface Props extends Omit<AppShellProps, 'children'> {
  checkIns: CheckInRecord[]
  zones: Zone[]
  currentZoneId: number | null
  urls: AppShellProps['urls'] & {
    checkIns: string
    newCheckIn: string
    exportCsv: string
    exportXlsx: string
    zones: string
  }
  translations: AppShellProps['translations'] & {
    checkIns: string
    user: string
    firstName: string
    lastName: string
    organization: string
    position: string
    email: string
    checkInTime: string
    zone: string
    allZones: string
    newCheckIn: string
    noCheckIns: string
    backToZones: string
    exportCsv: string
    exportXlsx: string
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Index(props: Props) {
  const { checkIns, zones, currentZoneId, urls, translations: t, ...shell } = props
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search.trim()) return checkIns
    const q = search.toLowerCase()
    return checkIns.filter(
      (ci) =>
        ci.firstName?.toLowerCase().includes(q) ||
        ci.lastName?.toLowerCase().includes(q) ||
        ci.email?.toLowerCase().includes(q) ||
        ci.organization?.toLowerCase().includes(q) ||
        ci.token?.toLowerCase().includes(q),
    )
  }, [checkIns, search])

  function handleZoneFilter(zoneId: number | null) {
    router.visit(urls.checkIns, {
      data: zoneId ? { zone_id: zoneId } : {},
      preserveState: true,
    })
  }

  function buildExportUrl(base: string) {
    const params = new URLSearchParams()
    if (search.trim()) params.set('search', search.trim())
    if (currentZoneId) params.set('zone_id', String(currentZoneId))
    const qs = params.toString()
    return qs ? `${base}?${qs}` : base
  }

  function exportCsv() {
    window.location.href = buildExportUrl(urls.exportCsv)
  }

  function exportXlsx() {
    window.location.href = buildExportUrl(urls.exportXlsx)
  }

  return (
    <AppShell {...shell} urls={urls} translations={t}>
      <Head title={t.checkIns} />

      <div className="mx-auto max-w-6xl py-8 px-4">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => router.visit(urls.zones)}
              className="text-[#91918E] hover:bg-[#F1F1EF]"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-semibold text-[#37352F]">{t.checkIns}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-[#37352F]"
              onClick={exportCsv}
            >
              <Download className="mr-2 h-4 w-4" />
              {t.exportCsv}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-[#37352F]"
              onClick={exportXlsx}
            >
              <Download className="mr-2 h-4 w-4" />
              {t.exportXlsx}
            </Button>
            <Button
              className="bg-[#2383E2] hover:bg-[#1b6ec2]"
              onClick={() => router.visit(urls.newCheckIn)}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t.newCheckIn}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center mb-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#91918E]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-9"
            />
          </div>

          {zones.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              <Button
                variant={currentZoneId ? 'outline' : 'default'}
                size="sm"
                className={currentZoneId ? '' : 'bg-[#2383E2] hover:bg-[#1b6ec2]'}
                onClick={() => handleZoneFilter(null)}
              >
                {t.allZones}
              </Button>
              {zones.map((z) => (
                <Button
                  key={z.id}
                  variant={currentZoneId === z.id ? 'default' : 'outline'}
                  size="sm"
                  className={currentZoneId === z.id ? 'bg-[#2383E2] hover:bg-[#1b6ec2]' : ''}
                  onClick={() => handleZoneFilter(z.id)}
                >
                  {z.name || `Zone ${z.id}`}
                </Button>
              ))}
            </div>
          )}
        </div>

        {/* Table */}
        {filtered.length > 0 ? (
          <div className="rounded-lg border border-[#E8E8E5] overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F1F1EF]">
                  <TableHead className="text-[#37352F] font-medium">{t.user}</TableHead>
                  <TableHead className="text-[#37352F] font-medium">{t.firstName}</TableHead>
                  <TableHead className="text-[#37352F] font-medium">{t.lastName}</TableHead>
                  <TableHead className="text-[#37352F] font-medium">{t.organization}</TableHead>
                  <TableHead className="text-[#37352F] font-medium">{t.position}</TableHead>
                  <TableHead className="text-[#37352F] font-medium">{t.email}</TableHead>
                  <TableHead className="text-[#37352F] font-medium">{t.checkInTime}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((ci) => (
                  <TableRow key={ci.id} className="hover:bg-[#F1F1EF]">
                    <TableCell className="text-xs text-[#91918E] font-mono">{ci.token}</TableCell>
                    <TableCell className="text-sm text-[#37352F]">{ci.firstName}</TableCell>
                    <TableCell className="text-sm text-[#37352F]">{ci.lastName}</TableCell>
                    <TableCell className="text-sm text-[#37352F]">{ci.organization}</TableCell>
                    <TableCell className="text-sm text-[#37352F]">{ci.position}</TableCell>
                    <TableCell className="text-sm text-[#37352F]">{ci.email}</TableCell>
                    <TableCell className="text-sm text-[#37352F] whitespace-nowrap">
                      <Clock className="mr-1 inline h-3 w-3 text-[#91918E]" />
                      {ci.checkInTime}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-16 text-[#91918E]">
            <Users className="mx-auto mb-3 h-10 w-10" />
            <p className="text-sm">{t.noCheckIns}</p>
          </div>
        )}
      </div>
    </AppShell>
  )
}
